import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { FEED_KEY_VERSION, getOrCreateFeedKey, wrapFeedKeyForUsername } from '../lib/feed-key';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';

type CircleSummary = { circleId: string; name: string; role: 'owner' | 'member'; joinedAt: string };
type CircleInvite = {
  circleId: string;
  circleName: string;
  inviterUserId: string;
  inviterUsername: string;
  invitedAt: string;
};

function roleLabel(role: 'owner' | 'member'): string {
  return role === 'owner' ? t('オーナー', 'Owner') : t('メンバー', 'Member');
}

export function CirclePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState<CircleSummary[]>([]);
  const [invites, setInvites] = useState<CircleInvite[]>([]);
  const [activeCircleId, setActiveCircleId] = useState<string | null>(null);
  const [activeCircleName, setActiveCircleName] = useState<string>('');

  const [newCircleName, setNewCircleName] = useState('');
  const [creatingCircle, setCreatingCircle] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [circles, active] = await Promise.all([api.circle.getMyCircles(), api.circle.getActiveCircle()]);
      setJoined(circles.joined);
      setInvites(circles.invites);
      setActiveCircleId(circles.activeCircleId);
      setActiveCircleName(active?.name ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('サークル情報の取得に失敗しました', 'Failed to load circles'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const hasSession = Boolean(localStorage.getItem('accessToken') && localStorage.getItem('userId'));
    if (!hasSession) {
      navigate('/', { replace: true });
      return;
    }

    if (!localStorage.getItem('identitySkRaw')) {
      navigate('/unlock', { replace: true });
      return;
    }

    void load();
  }, [load, navigate]);

  const handleCreateCircle = async () => {
    const name = newCircleName.trim();
    if (!name) {
      return;
    }

    setCreatingCircle(true);
    setError(null);
    try {
      const feedKey = await getOrCreateFeedKey(FEED_KEY_VERSION);
      const identitySkRaw = localStorage.getItem('identitySkRaw');
      if (!identitySkRaw) {
        throw new Error(t('ローカル秘密鍵がありません。再ログインしてください。', 'Local identity key missing. Please login again.'));
      }
      const me = await api.auth.me();
      const meIdentity = await api.user.getIdentityPk({ username: me.scratchUsername });
      const wrapped = await wrapFeedKeyForUsername(feedKey, meIdentity.identityPk);

      const created = await api.circle.createCircle({
        name,
        initialWrappedFeedKey: {
          version: FEED_KEY_VERSION,
          ciphertext: wrapped.ciphertext,
          ephemeralPk: wrapped.ephemeralPk
        }
      });

      await api.circle.setActiveCircle({ circleId: created.circleId });
      setNewCircleName('');
      await load();
      navigate(`/${created.circleId}/timeline`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('サークル作成に失敗しました', 'Failed to create circle'));
    } finally {
      setCreatingCircle(false);
    }
  };

  const handleSwitchCircle = async (circleId: string) => {
    if (circleId === activeCircleId) {
      navigate(`/${circleId}/timeline`);
      return;
    }

    try {
      await api.circle.setActiveCircle({ circleId });
      await load();
      navigate(`/${circleId}/timeline`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('サークル切り替えに失敗しました', 'Failed to switch circle'));
    }
  };

  const handleAcceptInvite = async (circleId: string) => {
    try {
      await api.circle.acceptInvite({ circleId });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('招待の承認に失敗しました', 'Failed to accept invite'));
    }
  };

  const handleDeclineInvite = async (circleId: string) => {
    try {
      await api.circle.declineInvite({ circleId });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('招待の辞退に失敗しました', 'Failed to decline invite'));
    }
  };

  if (loading) {
    return (
      <main className="mx-auto min-h-screen w-full max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-3xl space-y-4 px-4 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">{t('サークル', 'Circles')}</h1>
        <p className="text-sm text-muted-foreground">{t('サークル作成と参加中サークルの切り替えを行います。', 'Create circles and switch between joined circles.')}</p>
      </header>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('サークルを作成', 'Create Circle')}</CardTitle>
          <CardDescription>{t('ログイン後はサークル作成か招待承認が必要です。', 'After login, you must create or join a circle.')}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input value={newCircleName} onChange={(e) => setNewCircleName(e.target.value)} placeholder={t('サークル名', 'Circle name')} />
          <Button onClick={handleCreateCircle} disabled={creatingCircle || !newCircleName.trim()}>
            {creatingCircle ? t('作成中...', 'Creating...') : t('作成', 'Create')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('受信した招待', 'Incoming Invites')}</CardTitle>
        </CardHeader>
        <CardContent>
          {invites.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('招待はありません。', 'No invites.')}</p>
          ) : (
            <div className="space-y-2">
              {invites.map((invite) => (
                <div key={invite.circleId} className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">{invite.circleName}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('招待者', 'Inviter')}: {invite.inviterUsername} / {new Date(invite.invitedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => void handleAcceptInvite(invite.circleId)}>{t('承認', 'Accept')}</Button>
                    <Button size="sm" variant="outline" onClick={() => void handleDeclineInvite(invite.circleId)}>{t('辞退', 'Decline')}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('参加中サークル', 'Joined Circles')}</CardTitle>
          <CardDescription>
            {activeCircleName ? `${t('現在', 'Current')}: ${activeCircleName}` : t('アクティブサークルが未選択です。', 'No active circle selected.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {joined.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('参加中サークルはありません。', 'You have not joined any circles yet.')}</p>
          ) : (
            <div className="space-y-2">
              {joined.map((circle) => (
                <button
                  key={circle.circleId}
                  type="button"
                  className={`flex w-full items-center justify-between rounded-md border p-3 text-left transition-colors ${circle.circleId === activeCircleId ? 'border-primary/50 bg-primary/5' : 'border-border hover:bg-muted'}`}
                  onClick={() => void handleSwitchCircle(circle.circleId)}
                >
                  <span>
                    <span className="block text-sm font-medium">{circle.name}</span>
                    <span className="block text-xs text-muted-foreground">{roleLabel(circle.role)} / {new Date(circle.joinedAt).toLocaleString()}</span>
                  </span>
                  <span className={`rounded-md px-3 py-1 text-xs ${circle.circleId === activeCircleId ? 'border border-primary/40 text-primary' : 'bg-primary text-primary-foreground'}`}>
                    {circle.circleId === activeCircleId ? t('選択中', 'Active') : t('選択', 'Select')}
                  </span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
